import { formatCustomTranslations, getTranslation, parseLocale, resolveFormattingLocale } from './utils';
import { getLocalisedAmount } from '../utils/amount-util';
import { CDN_SUPPORTED_LOCALES, DEFAULT_LOCALE } from './constants';
import enUS from '../../../server/translations/en-US.json';

import type { CustomTranslations, LanguageOptions, Translations } from './types';
import type { ILanguageService } from './LanguageService';

export class Language {
    public readonly languageCode: string;
    private readonly service: ILanguageService;

    /**
     * Locale used to format amounts and dates, and to be passed on to third-party SDKs
     */
    private readonly _formattingLocale: string;

    /**
     * Locale of the translations that are loaded
     */
    private readonly _translationsLocale: string;

    private readonly customTranslations: CustomTranslations;

    /**
     * Supported locales list
     * Includes all supported locales from the CDN and any custom locales defined in customTranslations
     */
    private readonly supportedLocales: readonly string[];

    /**
     * Translations object
     * Contains all translations for the current locale, including English as fallback
     */
    private _translations: Translations = {};

    public readonly timeFormatOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric'
    };

    public readonly timeAndDateFormatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...this.timeFormatOptions
    };

    public readonly timeAndDateFormatter: Intl.DateTimeFormat;

    constructor(props: LanguageOptions) {
        const { locale, customTranslations, service } = props;

        this.service = service;

        this.customTranslations = formatCustomTranslations(customTranslations);
        this.supportedLocales = this.createSupportedLocalesList(this.customTranslations);

        this._translationsLocale = parseLocale(locale || DEFAULT_LOCALE, this.supportedLocales);
        this._formattingLocale = resolveFormattingLocale(locale || DEFAULT_LOCALE);
        this.languageCode = this._translationsLocale.split('-')[0];

        this.timeAndDateFormatter = Intl.DateTimeFormat(this._formattingLocale, this.timeAndDateFormatOptions);
    }

    /**
     * Returns the locale requested by the merchant, in the format Intl accepts.
     * Unlike {@link Language.translationsLocale}, it is not limited to the locales we ship translations for.
     */
    public get locale(): string {
        return this._formattingLocale;
    }

    /**
     * Returns the locale of the translations that are loaded.
     * Limited to the locales we ship translations for, so a request for an unsupported one resolves to the closest match.
     */
    public get translationsLocale(): string {
        return this._translationsLocale;
    }

    public async requestTranslations(): Promise<void> {
        const translations = await this.service.fetchTranslationsFromCdn(this._translationsLocale);

        this._translations = {
            ...enUS,
            ...translations,
            ...(Boolean(this.customTranslations[this._translationsLocale]) && this.customTranslations[this._translationsLocale])
        };
    }

    public get translations(): Readonly<Translations> {
        return this._translations;
    }

    /**
     * Returns a translated string from a key in the current {@link Language.translationsLocale}
     * @param key - Translation key
     * @param options - Translation options
     * @returns Translated string
     */
    public get(key: string, options?): string {
        const translation = getTranslation(this._translations, key, options);
        if (translation !== null) {
            return translation;
        }

        return key;
    }

    /**
     * Returns a localized string for an amount
     * @param amount - Amount to be converted
     * @param currencyCode - Currency code of the amount
     * @param options - Options for String.prototype.toLocaleString
     */
    public amount(amount: number, currencyCode: string, options?: object): string {
        return getLocalisedAmount(amount, this._formattingLocale, currencyCode, options);
    }

    /**
     * Returns a localized string for a date
     * @param date - Date to be localized
     * @param options - Options for {@link Intl.DateTimeFormatOptions}
     */
    public date(date: string, options: Intl.DateTimeFormatOptions = {}) {
        if (date === undefined) return '';
        const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', ...options };
        return new Date(date).toLocaleDateString(this._formattingLocale, dateOptions);
    }

    /**
     * Returns a localized string for a date and time
     * @param date - Date to be localized
     */
    public dateTime(date: string) {
        if (date === undefined) return '';
        return this.timeAndDateFormatter.format(new Date(date));
    }

    /**
     * Creates a list of supported locales including the ones passed as custom translations
     *
     * @param customTranslations - Custom translations to include
     * @returns Array of supported locales
     */
    private createSupportedLocalesList(customTranslations?: CustomTranslations): readonly string[] {
        if (!customTranslations) {
            return CDN_SUPPORTED_LOCALES;
        }

        const locales = new Set([...CDN_SUPPORTED_LOCALES, ...Object.keys(customTranslations)]);
        return Array.from(locales).sort((a, b) => a.localeCompare(b));
    }
}

export default Language;
